import { useEffect, useMemo, useState } from 'react'
import {
  adminApi,
  type AdminUser,
  type MarketingChannel,
  type MarketingCategory,
  type MarketingConfig,
  type MarketingDraft,
  type MarketingAudiencePreview,
  type MarketingSendResult,
  type MarketingCampaignSummary,
  type FunnelResponse,
  type KeywordsResponse,
} from '../api/endpoints'
import { ApiError } from '../api/types'

/**
 * Admin → Marketing (R36). "The dad flow": type a plain-English brief, pick a
 * channel + audience category, generate an AI draft (subject + branded email +
 * WhatsApp text + real stone picks), review/edit, then Send.
 *
 * HARD SAFETY: while `armed` is false every send is redirected to the allowlist
 * and prefixed [TEST]. The Arm toggle is the only way to reach real audiences.
 * Self-contained so it drops into Admin.tsx as one <AdminMarketing /> tab.
 */

const CHANNELS: { value: MarketingChannel; label: string }[] = [
  { value: 'email', label: 'Email' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'both', label: 'Email + WhatsApp' },
]
const CATEGORIES: { value: MarketingCategory; label: string; hint: string }[] = [
  { value: 'usa', label: 'USA', hint: 'US-based users' },
  { value: 'worldwide', label: 'Worldwide', hint: 'everyone reachable' },
  { value: 'suppliers', label: 'Suppliers', hint: 'supplier accounts' },
  { value: 'selected-users', label: 'Selected users', hint: 'pick individuals' },
]

const fmtUsd = (n: number) => `$${Math.round(Math.max(0, n)).toLocaleString()}`
const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—')

export default function AdminMarketing() {
  const [config, setConfig] = useState<MarketingConfig | null>(null)
  const [prompt, setPrompt] = useState('')
  const [channel, setChannel] = useState<MarketingChannel>('email')
  const [category, setCategory] = useState<MarketingCategory>('worldwide')
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])

  const [draft, setDraft] = useState<MarketingDraft | null>(null)
  const [subject, setSubject] = useState('')
  const [bodyHtml, setBodyHtml] = useState('')
  const [whatsappText, setWhatsappText] = useState('')
  const [editHtml, setEditHtml] = useState(false)

  const [preview, setPreview] = useState<MarketingAudiencePreview | null>(null)
  const [sendResult, setSendResult] = useState<MarketingSendResult | null>(null)
  const [campaigns, setCampaigns] = useState<MarketingCampaignSummary[]>([])

  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [flash, setFlash] = useState<string | null>(null)

  async function loadConfig() {
    try { setConfig(await adminApi.marketing.armed()) } catch { /* non-fatal */ }
  }
  async function loadCampaigns() {
    try { setCampaigns((await adminApi.marketing.campaigns()).campaigns) } catch { /* non-fatal */ }
  }
  useEffect(() => { loadConfig(); loadCampaigns() }, [])

  const armed = config?.armed === true

  async function generate() {
    setError(null); setFlash(null); setSendResult(null); setPreview(null)
    setBusy('draft')
    try {
      const d = await adminApi.marketing.draft({
        prompt: prompt.trim(),
        channel,
        category,
        userIds: category === 'selected-users' ? selectedUserIds : undefined,
      })
      setDraft(d)
      setSubject(d.subject)
      setBodyHtml(d.bodyHtml)
      setWhatsappText(d.whatsappText)
      setEditHtml(false)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not generate draft')
    } finally { setBusy(null) }
  }

  async function saveEdits() {
    if (!draft) return
    setBusy('save'); setError(null); setFlash(null)
    try {
      await adminApi.marketing.update({ campaignId: draft.campaignId, subject, bodyHtml, whatsappText })
      setFlash('Draft saved.')
      setTimeout(() => setFlash(null), 2500)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save draft')
    } finally { setBusy(null) }
  }

  async function previewAudience() {
    setBusy('preview'); setError(null)
    try {
      setPreview(await adminApi.marketing.previewAudience({
        category,
        userIds: category === 'selected-users' ? selectedUserIds : undefined,
      }))
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not preview audience')
    } finally { setBusy(null) }
  }

  async function send() {
    if (!draft) return
    const warn = armed
      ? 'Send to the REAL audience? This emails/messages live users.'
      : 'Send in TEST mode? Goes only to the allowlist, prefixed [TEST].'
    if (!window.confirm(warn)) return
    setBusy('send'); setError(null); setFlash(null)
    try {
      // Persist any last edits, then send.
      await adminApi.marketing.update({ campaignId: draft.campaignId, subject, bodyHtml, whatsappText })
      const r = await adminApi.marketing.send(draft.campaignId)
      setSendResult(r)
      setFlash(r.testMode
        ? `TEST send complete — ${r.recipients} allowlist recipient(s). Real audience would be ${r.audienceResolvedCount}.`
        : `Sent to ${r.recipients} recipient(s) · ${r.sentCount} ok, ${r.failedCount} failed.`)
      await loadCampaigns()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not send campaign')
    } finally { setBusy(null) }
  }

  async function toggleArm(next: boolean) {
    const msg = next
      ? 'ARM real sends? After this, campaigns go to real users, not the allowlist.'
      : 'Disarm — return to TEST mode (allowlist only)?'
    if (!window.confirm(msg)) return
    setBusy('arm'); setError(null)
    try {
      setConfig(await adminApi.marketing.arm(next))
      setFlash(next ? 'Real sends ARMED.' : 'Back to TEST mode.')
      setTimeout(() => setFlash(null), 2500)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not change arm state')
    } finally { setBusy(null) }
  }

  return (
    <div className="space-y-5" data-testid="admin-marketing">
      <StatsStrip />

      <ArmBanner armed={armed} allowlist={config?.allowlistEmails ?? []} busy={busy === 'arm'} onToggle={toggleArm} />

      {error && <div className="rounded-card border border-danger/30 bg-danger/5 text-danger text-sm p-3">{error}</div>}
      {flash && <div className="rounded-card border border-success/30 bg-success/10 text-success text-sm p-3">{flash}</div>}

      <div className="grid gap-4 lg:grid-cols-3 items-start">
        {/* ── AI marketing assistant ─────────────────────────── */}
        <section className="card space-y-4">
          <div className="flex items-center gap-2">
            <h2 className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink">AI Marketing Assistant</h2>
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider text-primary">Beta</span>
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-soft">Prompt</span>
            <textarea
              rows={4}
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="Describe what you want to send and who to send it to…"
              className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2 text-sm text-ink resize-none placeholder:text-soft"
              data-testid="marketing-prompt"
            />
          </label>

          {category === 'selected-users' && (
            <UserPicker selected={selectedUserIds} onChange={setSelectedUserIds} />
          )}

          <button
            type="button"
            onClick={generate}
            disabled={!prompt.trim() || busy === 'draft' || (category === 'selected-users' && selectedUserIds.length === 0)}
            className="btn-primary w-full disabled:opacity-50"
            data-testid="marketing-generate"
          >
            {busy === 'draft' ? 'Drafting…' : 'Generate Campaign'}
          </button>
        </section>

        {/* ── Campaign settings ──────────────────────────────── */}
        <section className="card space-y-5">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink">Campaign Settings</h2>

          <div>
            <span className="text-[10px] uppercase tracking-wider text-soft">Channel</span>
            <div className="mt-1.5 flex flex-wrap gap-2" data-testid="marketing-channel" role="radiogroup" aria-label="Channel">
              {CHANNELS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  role="radio"
                  aria-checked={channel === c.value}
                  onClick={() => setChannel(c.value)}
                  className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                    channel === c.value ? 'border-line bg-navy/5 text-ink' : 'border-border bg-paper text-muted hover:text-ink'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <span className="text-[10px] uppercase tracking-wider text-soft">Audience</span>
            <div className="mt-1.5 flex flex-wrap gap-2" data-testid="marketing-category" role="radiogroup" aria-label="Audience">
              {CATEGORIES.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  role="radio"
                  aria-checked={category === c.value}
                  onClick={() => { setCategory(c.value); setPreview(null) }}
                  title={c.hint}
                  className={`rounded-full border px-3.5 py-1.5 text-sm font-medium transition-colors ${
                    category === c.value ? 'border-line bg-navy text-white' : 'border-border bg-paper text-muted hover:text-ink'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {category === 'selected-users' && (
            <UserPicker selected={selectedUserIds} onChange={setSelectedUserIds} />
          )}

          <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
            <button
              type="button"
              onClick={previewAudience}
              disabled={busy === 'preview' || (category === 'selected-users' && selectedUserIds.length === 0)}
              className="rounded-full border border-border bg-paper px-4 py-2 text-sm font-semibold text-ink hover:bg-page disabled:opacity-50"
              data-testid="marketing-preview-audience"
            >
              {busy === 'preview' ? 'Checking…' : 'Preview audience'}
            </button>
            {preview && (
              <span className="text-xs text-muted text-right" data-testid="marketing-audience-count">
                {preview.count} user(s) · {preview.withEmail} email · {preview.withPhone} phone
              </span>
            )}
          </div>
        </section>

        {/* ── Campaign preview ───────────────────────────────── */}
        <section className="card space-y-3">
          <h2 className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink">Campaign Preview</h2>
          <div className="overflow-hidden rounded-xl border border-border bg-cream">
            <div className="relative flex items-stretch">
              <div className="flex-1 p-4">
                <p className="font-display text-lg leading-none text-ink">DiaEx</p>
                <p className="mt-0.5 text-[8px] uppercase tracking-[0.2em] text-gold">by Aska</p>
                <p className="mt-3 font-display text-xl font-semibold leading-tight text-ink">
                  New Pear Arrivals<br />Now Live
                </p>
                <p className="mt-2 text-[11px] text-muted">1–2ct Pear Shaped Diamonds</p>
                <p className="text-[11px] text-muted">Excellent Cuts · IGI Certified · Best Prices</p>
                <span className="mt-3 inline-block rounded-md bg-gold px-3 py-1.5 text-xs font-semibold text-white">
                  View Collection
                </span>
              </div>
              <div
                className="w-28 shrink-0 bg-cover bg-center"
                style={{ backgroundImage: 'url(/diamonds/pear.webp)' }}
                role="img"
                aria-label="Pear-shaped diamond"
              />
            </div>
          </div>
          <p className="text-[11px] text-soft">Live preview — the generated draft renders below once ready.</p>
        </section>
      </div>

      {draft && (
        <section className="card space-y-4" data-testid="marketing-draft">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-bold text-ink">Review &amp; edit</h2>
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${draft.aiGenerated ? 'bg-primary/10 text-primary' : 'bg-warn/15 text-warn'}`}>
              {draft.aiGenerated ? 'AI-drafted' : 'Fallback draft'}
            </span>
          </div>

          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-soft">Subject</span>
            <input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="mt-1 w-full h-10 rounded-lg border border-border bg-paper px-3 text-sm text-ink"
              data-testid="marketing-subject"
            />
          </label>

          {(channel === 'email' || channel === 'both') && (
            <div>
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-wider text-soft">Email preview</span>
                <button type="button" onClick={() => setEditHtml((v) => !v)} className="text-xs font-semibold text-primary">
                  {editHtml ? 'Show preview' : 'Edit HTML'}
                </button>
              </div>
              {editHtml ? (
                <textarea
                  rows={10}
                  value={bodyHtml}
                  onChange={(e) => setBodyHtml(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-border bg-paper px-3 py-2 text-xs font-mono text-ink"
                  data-testid="marketing-body-html"
                />
              ) : (
                <iframe
                  title="Email preview"
                  srcDoc={bodyHtml}
                  className="mt-1 w-full h-96 rounded-lg border border-border bg-paper"
                  sandbox=""
                  data-testid="marketing-body-preview"
                />
              )}
            </div>
          )}

          {(channel === 'whatsapp' || channel === 'both') && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider text-soft">WhatsApp message</span>
              <textarea
                rows={5}
                value={whatsappText}
                onChange={(e) => setWhatsappText(e.target.value)}
                className="mt-1 w-full rounded-lg border border-border bg-success/10 px-3 py-2 text-sm text-ink"
                data-testid="marketing-whatsapp"
              />
            </label>
          )}

          {draft.stonePickDetails.length > 0 && (
            <div>
              <span className="text-[10px] uppercase tracking-wider text-soft">Stone picks ({draft.stonePickDetails.length})</span>
              <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {draft.stonePickDetails.map((p) => (
                  <a key={p.uniqueId} href={p.url} target="_blank" rel="noreferrer" className="flex items-center gap-2 rounded-lg border border-border p-2 hover:bg-page">
                    {p.imageUrl && <img src={p.imageUrl} alt="" className="h-12 w-12 rounded object-cover" />}
                    <div className="min-w-0">
                      <p className="truncate text-xs font-semibold text-ink">{[p.shape, p.carat ? `${p.carat}ct` : null, p.color, p.clarity].filter(Boolean).join(' ')}</p>
                      <p className="text-xs font-bold text-primary">{fmtUsd(p.priceUSD)}</p>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border pt-3">
            {!armed && <span className="mr-auto text-xs text-warn">TEST mode — allowlist only</span>}
            <button type="button" onClick={saveEdits} disabled={busy === 'save'} className="rounded-full border border-border bg-paper px-4 py-2 text-sm font-semibold text-ink hover:bg-page disabled:opacity-50" data-testid="marketing-save">
              {busy === 'save' ? 'Saving…' : 'Save Draft'}
            </button>
            <button type="button" onClick={send} disabled={busy === 'send' || draft.status === 'sent'} className="btn-primary px-5 disabled:opacity-50" data-testid="marketing-send">
              {busy === 'send' ? 'Sending…' : armed ? 'Send Campaign' : 'Send TEST (allowlist)'}
            </button>
          </div>

          {sendResult && (
            <div className="rounded-lg border border-border bg-page p-3 text-sm" data-testid="marketing-send-result">
              <p className="font-semibold text-ink">
                {sendResult.testMode ? 'TEST send' : 'Live send'} — {sendResult.recipients} recipient(s), {sendResult.sentCount} ok, {sendResult.failedCount} failed
              </p>
              <ul className="mt-2 space-y-1 text-xs text-muted">
                {sendResult.results.map((r, i) => (
                  <li key={i}>
                    {r.email || r.phone || '—'}
                    {r.emailStatus && ` · email:${r.emailStatus}`}
                    {r.whatsappStatus && ` · wa:${r.whatsappStatus}`}
                    {(r.emailError || r.whatsappError) && <span className="text-danger"> ({r.emailError || r.whatsappError})</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>
      )}

      <CampaignHistory campaigns={campaigns} />
    </div>
  )
}

function ArmBanner({ armed, allowlist, busy, onToggle }: {
  armed: boolean
  allowlist: string[]
  busy: boolean
  onToggle: (next: boolean) => void
}) {
  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-card border p-3 text-sm ${armed ? 'border-danger/40 bg-danger/5' : 'border-warn/40 bg-warn/10'}`}
      data-testid="marketing-arm-banner"
    >
      <div>
        <p className={`font-bold ${armed ? 'text-danger' : 'text-warn'}`}>
          {armed ? 'ARMED — sends reach REAL users' : 'TEST mode — sends go to the allowlist only'}
        </p>
        {!armed && <p className="mt-0.5 text-xs text-warn">Allowlist: {allowlist.join(', ') || '—'}. Subjects are prefixed [TEST].</p>}
      </div>
      <button
        type="button"
        onClick={() => onToggle(!armed)}
        disabled={busy}
        className={`rounded-full px-4 py-2 text-xs font-bold disabled:opacity-50 ${armed ? 'bg-paper border border-danger text-danger' : 'bg-danger text-white'}`}
        data-testid="marketing-arm-toggle"
      >
        {busy ? '…' : armed ? 'Disarm (back to TEST)' : 'Arm real sends'}
      </button>
    </div>
  )
}

function UserPicker({ selected, onChange }: { selected: string[]; onChange: (ids: string[]) => void }) {
  const [users, setUsers] = useState<AdminUser[]>([])
  const [q, setQ] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    adminApi.users().then((r) => setUsers(r.users)).catch(() => {}).finally(() => setLoading(false))
  }, [])

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase()
    const rows = users.filter((u) => !!u.email)
    if (!s) return rows.slice(0, 50)
    return rows.filter((u) =>
      String(u.email || '').toLowerCase().includes(s) ||
      String(u.companyName || u.displayName || '').toLowerCase().includes(s)).slice(0, 50)
  }, [users, q])

  const keyOf = (u: AdminUser) => String(u.userId ?? u.uid)

  function toggle(u: AdminUser) {
    const k = keyOf(u)
    onChange(selected.includes(k) ? selected.filter((x) => x !== k) : [...selected, k])
  }

  return (
    <div className="rounded-lg border border-border p-3" data-testid="marketing-user-picker">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-soft">Select users ({selected.length} chosen)</span>
        {selected.length > 0 && <button type="button" onClick={() => onChange([])} className="text-xs text-primary">Clear</button>}
      </div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search by email or company…"
        className="mt-1 w-full h-9 rounded-lg border border-border bg-paper px-3 text-sm text-ink"
        data-testid="marketing-user-search"
      />
      <div className="mt-2 max-h-52 overflow-y-auto divide-y divide-[#f0f2f6]">
        {loading ? <p className="py-3 text-sm text-muted">Loading users…</p>
          : filtered.length === 0 ? <p className="py-3 text-sm text-muted">No matching users.</p>
          : filtered.map((u) => {
            const k = keyOf(u)
            return (
              <label key={u.uid} className="flex items-center gap-2 py-1.5 text-sm cursor-pointer">
                <input type="checkbox" checked={selected.includes(k)} onChange={() => toggle(u)} />
                <span className="text-ink">{u.companyName || u.displayName || u.email}</span>
                <span className="text-xs text-muted">{u.email}</span>
              </label>
            )
          })}
      </div>
    </div>
  )
}

function CampaignHistory({ campaigns }: { campaigns: MarketingCampaignSummary[] }) {
  return (
    <section className="card space-y-3" data-testid="marketing-history">
      <h2 className="text-base font-bold text-ink">Campaign history</h2>
      {campaigns.length === 0 ? (
        <p className="text-sm text-muted">No campaigns yet. Compose one above.</p>
      ) : (
        <div className="overflow-x-auto -mx-1">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] uppercase tracking-wider text-soft border-b border-border">
                <th className="py-2 px-2 font-semibold">Subject</th>
                <th className="py-2 px-2 font-semibold">Channel</th>
                <th className="py-2 px-2 font-semibold">Audience</th>
                <th className="py-2 px-2 font-semibold">Status</th>
                <th className="py-2 px-2 font-semibold text-right">Sent</th>
                <th className="py-2 px-2 font-semibold">When</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr key={c.campaignId} className="border-b border-[#f0f2f6]" data-testid={`marketing-campaign-${c.campaignId}`}>
                  <td className="py-2 px-2 text-ink max-w-[240px] truncate">{c.subject || '(no subject)'}</td>
                  <td className="py-2 px-2 text-muted">{c.channel}</td>
                  <td className="py-2 px-2 text-muted">{c.category}</td>
                  <td className="py-2 px-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${c.status === 'sent' ? (c.testMode ? 'bg-warn/15 text-warn' : 'bg-success/15 text-success') : 'bg-page text-soft'}`}>
                      {c.status}{c.status === 'sent' && c.testMode ? ' · test' : ''}
                    </span>
                  </td>
                  <td className="py-2 px-2 text-right tabular-nums text-ink">{c.sentCount}{c.failedCount ? ` / ${c.failedCount}✗` : ''}</td>
                  <td className="py-2 px-2 text-muted">{fmtDate(c.sentAt || c.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

/**
 * Platform stats, folded in from the retired Stats tab (2026-09-20): the
 * signup→enquiry funnel + top searched shapes, over a pickable range. Lives
 * at the top of Marketing because "what are people doing" is the question you
 * ask right before "who do I message about it". Collapsed by default — the
 * marketing tools are the tab's job; the numbers are context.
 */
function StatsStrip() {
  const [open, setOpen] = useState(false)
  const [range, setRange] = useState<'7d' | '30d' | '90d'>('30d')
  const [funnel, setFunnel] = useState<FunnelResponse | null>(null)
  const [keywords, setKeywords] = useState<KeywordsResponse | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    Promise.all([adminApi.analytics.funnel(range), adminApi.analytics.keywords(range)])
      .then(([f, k]) => { if (!cancelled) { setFunnel(f); setKeywords(k); setError(null) } })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof ApiError ? e.message : ((e as Error)?.message || 'Could not load stats'))
      })
    return () => { cancelled = true }
  }, [open, range])

  return (
    <section className="card space-y-3" data-testid="admin-stats-strip">
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.14em] text-ink"
          data-testid="admin-stats-toggle"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            strokeLinecap="round" className={`transition-transform ${open ? 'rotate-90' : ''}`}>
            <path d="m9 18 6-6-6-6" />
          </svg>
          Platform stats
        </button>
        {open && (['7d', '30d', '90d'] as const).map((r) => (
          <button
            key={r}
            type="button"
            onClick={() => setRange(r)}
            className={`rounded-full px-3 py-1 text-xs font-semibold ${range === r ? 'bg-primary text-white' : 'bg-page text-soft hover:text-ink'}`}
          >
            {r}
          </button>
        ))}
        {!open && <span className="text-xs text-muted">funnel + top searched shapes</span>}
      </div>
      {open && (
        error ? <div className="rounded-card border border-danger/30 bg-danger/5 text-danger text-sm p-3">{error}</div>
          : !funnel || !keywords ? <div className="h-24 animate-pulse rounded-xl bg-page" />
            : (
              <div className="grid gap-5 sm:grid-cols-2">
                <div>
                  <p className="mb-2 text-[10px] uppercase tracking-wider text-soft">Funnel (distinct users)</p>
                  <div className="space-y-1.5">
                    {funnel.funnel.map((s) => (
                      <div key={s.stage} className="flex items-center justify-between text-sm">
                        <span className="capitalize text-ink">{s.stage}</span>
                        <span className="tabular-nums font-semibold text-ink">{s.users.toLocaleString()}</span>
                      </div>
                    ))}
                    {funnel.funnel.length === 0 && <p className="text-sm text-muted">No events in range.</p>}
                  </div>
                </div>
                <div>
                  <p className="mb-2 text-[10px] uppercase tracking-wider text-soft">Top shapes searched</p>
                  <div className="space-y-1.5">
                    {keywords.shapes.slice(0, 8).map((t) => (
                      <div key={t.term} className="flex items-center justify-between text-sm">
                        <span className="text-ink">{t.term}</span>
                        <span className="tabular-nums font-semibold text-ink">{t.count.toLocaleString()}</span>
                      </div>
                    ))}
                    {keywords.shapes.length === 0 && <p className="text-sm text-muted">No search events in range.</p>}
                  </div>
                </div>
              </div>
            )
      )}
    </section>
  )
}
